import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  InputAdornment,
  Collapse,
  Fade,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Header from '../components/Header';
import Loader from '../components/Loader';
import {
  createAdmin,
  assignRole,
  getAllUsers,
  createWebsite,
  getWebsites,
  deleteUser,
  updateUser,
  removeWebsiteFromUser,
  deleteWebsite,
  getMe,
} from '../api/admin';
import { useHistory } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { color, font, bevel } from '../styles/tokens';

const consoleSx = {
  width: '100%',
  p: { xs: 2, md: 3 },
  background: color.panel,
  border: `1px solid ${color.ruleSoft}`,
  clipPath: bevel(12),
  color: color.bone,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  height: { xs: 'auto', md: 360 },
};

const fieldLabelSx = { style: { color: color.boneFaint, fontFamily: font.mono, fontSize: '.7rem', letterSpacing: '.08em', textTransform: 'uppercase' } };

const AdminDashboard = () => {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [newWebsite, setNewWebsite] = useState({ name: '', link: '' });
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '' });
  const [assignData, setAssignData] = useState({ userId: '', role: 'admin', websites: [] });
  const [userFilter, setUserFilter] = useState('');
  const [websiteFilter, setWebsiteFilter] = useState('');
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(5);
  const [websitePage, setWebsitePage] = useState(0);
  const [websiteRowsPerPage, setWebsiteRowsPerPage] = useState(5);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedWebsite, setExpandedWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [notice, setNotice] = useState(null);
  const [editUserTarget, setEditUserTarget] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ username: '', password: '', confirmPassword: '' });
  const [savingUser, setSavingUser] = useState(false);

  const token = localStorage.getItem('token');
  const history = useHistory();

  // Check role on mount
  useEffect(() => {
    if (!token) {
      history.push('/Enteria');
      return;
    }
    const decoded = jwtDecode(token);
    if (decoded.role !== 'superadmin') {
      history.push('/dashboard');
    }
  }, [token, history]);

  useEffect(() => {
    setLoading(true);
    if (token) {
      Promise.all([
        getMe(token).then(res => setMe(res.data)).catch(() => {}),
        getAllUsers(token)
          .then(res => setUsers(res.data))
          .catch(err => {
            if (err?.response?.data?.message === 'Invalid token.') {
              localStorage.removeItem('token');
              history.push('/Enteria');
            }
          }),
        getWebsites(token)
          .then(res => setWebsites(res.data))
          .catch(() => {}),
      ]).finally(() => setTimeout(() => setLoading(false), 600));
    }
  }, [token, history]);

  const handleCreateWebsite = async (e) => {
    e.preventDefault();
    if (!newWebsite.name.trim() || !newWebsite.link.trim()) {
      setNotice({ severity: 'error', message: 'Website name and link are required.' });
      return;
    }
    setLoading(true);
    try {
      await createWebsite(newWebsite, token);
      setNewWebsite({ name: '', link: '' });
      const res = await getWebsites(token);
      setWebsites(res.data);
      setNotice({ severity: 'success', message: 'Website created.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t create the website. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.username.trim() || newAdmin.password.length < 6) {
      setNotice({ severity: 'error', message: 'Username is required and password must be at least 6 characters.' });
      return;
    }
    setLoading(true);
    try {
      await createAdmin(
        { username: newAdmin.username, password: newAdmin.password },
        token
      );
      setNewAdmin({ username: '', password: '' });
      const res = await getAllUsers(token);
      setUsers(res.data);
      setNotice({ severity: 'success', message: 'Admin created.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t create the admin. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    if (!assignData.userId) {
      setNotice({ severity: 'error', message: 'Choose a user to assign.' });
      return;
    }
    setLoading(true);
    try {
      await assignRole(
        assignData.userId,
        { role: assignData.role, websites: assignData.websites },
        token
      );
      setAssignData({ userId: '', role: 'admin', websites: [] });
      const res = await getAllUsers(token);
      setUsers(res.data);
      setNotice({ severity: 'success', message: 'Role and properties updated.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t update the assignment. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    history.push('/Enteria');
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Remove this user?')) return;
    setLoading(true);
    try {
      await deleteUser(userId, token);
      const res = await getAllUsers(token);
      setUsers(res.data);
      setNotice({ severity: 'success', message: 'User removed.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t remove this user. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const openEditUser = (user) => {
    setEditUserTarget(user);
    setEditUserForm({ username: user.username, password: '', confirmPassword: '' });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    const { username, password, confirmPassword } = editUserForm;
    if (!username.trim()) {
      setNotice({ severity: 'error', message: 'Username is required.' });
      return;
    }
    if (password && password.length < 6) {
      setNotice({ severity: 'error', message: 'Password must be at least 6 characters.' });
      return;
    }
    if (password && password !== confirmPassword) {
      setNotice({ severity: 'error', message: 'Passwords don’t match.' });
      return;
    }
    setSavingUser(true);
    try {
      await updateUser(editUserTarget._id, { username: username.trim(), password: password || undefined }, token);
      const res = await getAllUsers(token);
      setUsers(res.data);
      setEditUserTarget(null);
      setNotice({ severity: 'success', message: 'Staff details updated.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t update this user. Please try again.' });
    } finally {
      setSavingUser(false);
    }
  };

  const handleRemoveWebsite = async (userId, websiteId) => {
    setLoading(true);
    try {
      await removeWebsiteFromUser(userId, websiteId, token);
      const res = await getAllUsers(token);
      setUsers(res.data);
      setNotice({ severity: 'success', message: 'Website access removed.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t remove that website. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWebsite = async (websiteId) => {
    if (!window.confirm('Delete this website? Posts filed against it will keep the reference.')) return;
    setLoading(true);
    try {
      await deleteWebsite(websiteId, token);
      const [websitesRes, usersRes] = await Promise.all([getWebsites(token), getAllUsers(token)]);
      setWebsites(websitesRes.data);
      setUsers(usersRes.data);
      setNotice({ severity: 'success', message: 'Website deleted.' });
    } catch (err) {
      setNotice({ severity: 'error', message: err?.response?.data?.message || 'Couldn’t delete this website. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyEndpoint = async (path) => {
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      setNotice({ severity: 'success', message: 'API endpoint copied.' });
    } catch {
      setNotice({ severity: 'error', message: 'Couldn’t copy — select and copy the endpoint manually.' });
    }
  };

  // Filtering and pagination
  const filteredUsers = users.filter(
    user =>
      user.username.toLowerCase().includes(userFilter.toLowerCase()) ||
      user.role.toLowerCase().includes(userFilter.toLowerCase())
  );
  const pagedUsers = filteredUsers.slice(userPage * userRowsPerPage, userPage * userRowsPerPage + userRowsPerPage);

  const filteredWebsites = websites.filter(
    site =>
      site.name.toLowerCase().includes(websiteFilter.toLowerCase()) ||
      site.link.toLowerCase().includes(websiteFilter.toLowerCase())
  );
  const pagedWebsites = filteredWebsites.slice(websitePage * websiteRowsPerPage, websitePage * websiteRowsPerPage + websiteRowsPerPage);

  // Responsive expand/collapse for tables — reactive to the viewport,
  // unlike a one-off window.innerWidth check made at render time.
  const isMobile = useMediaQuery('(max-width:900px)');

  // Tab content
  const CreateWebsiteCard = (
    <Paper sx={consoleSx}>
      <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.05rem' }}>
        Add a property
      </Typography>
      <Box component="form" onSubmit={handleCreateWebsite} sx={{ flex: 1 }}>
        <Stack spacing={2}>
          <TextField
            label="Name"
            value={newWebsite.name}
            onChange={e => setNewWebsite({ ...newWebsite, name: e.target.value })}
            required
            fullWidth
            InputLabelProps={fieldLabelSx}
          />
          <TextField
            label="Link"
            value={newWebsite.link}
            onChange={e => setNewWebsite({ ...newWebsite, link: e.target.value })}
            required
            fullWidth
            InputLabelProps={fieldLabelSx}
          />
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Add property
          </Button>
        </Stack>
      </Box>
    </Paper>
  );

  const CreateAdminCard = (
    <Paper sx={consoleSx}>
      <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.05rem' }}>
        Add an admin
      </Typography>
      <Box component="form" onSubmit={handleCreateAdmin} sx={{ flex: 1 }}>
        <Stack spacing={2}>
          <TextField
            label="Callsign"
            value={newAdmin.username}
            onChange={e => setNewAdmin({ ...newAdmin, username: e.target.value })}
            required
            fullWidth
            InputLabelProps={fieldLabelSx}
          />
          <TextField
            label="Passcode"
            type="password"
            value={newAdmin.password}
            onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })}
            helperText="At least 6 characters"
            required
            fullWidth
            InputLabelProps={fieldLabelSx}
          />
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Add admin
          </Button>
        </Stack>
      </Box>
    </Paper>
  );

  const AssignRoleCard = (
    <Paper sx={consoleSx}>
      <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.05rem' }}>
        Assign role &amp; properties
      </Typography>
      <Box component="form" onSubmit={handleAssignRole} sx={{ flex: 1 }}>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: color.boneFaint, fontFamily: font.mono, fontSize: '.75rem' }}>User</InputLabel>
            <Select
              value={assignData.userId}
              onChange={e => setAssignData({ ...assignData, userId: e.target.value })}
              required
              label="User"
            >
              <MenuItem value="">Select user</MenuItem>
              {users
                .filter(user => user.role !== 'superadmin')
                .map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.username} ({user.role})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel sx={{ color: color.boneFaint, fontFamily: font.mono, fontSize: '.75rem' }}>Role</InputLabel>
            <Select
              value={assignData.role}
              onChange={e => setAssignData({ ...assignData, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="superadmin">Superadmin</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel sx={{ color: color.boneFaint, fontFamily: font.mono, fontSize: '.75rem' }}>Properties</InputLabel>
            <Select
              multiple
              value={assignData.websites}
              onChange={e => setAssignData({ ...assignData, websites: Array.from(e.target.value) })}
              label="Properties"
              renderValue={selected =>
                websites.filter(site => selected.includes(site._id)).map(site => site.name).join(', ')
              }
            >
              {websites.map(site => (
                <MenuItem key={site._id} value={site._id}>
                  {site.name} ({site.link})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button type="submit" variant="contained" color="primary" fullWidth>
            Save assignment
          </Button>
        </Stack>
      </Box>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: color.ink }}>
      <Header user={me} onLogout={handleLogout} websiteCount={websites.length} />
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 4 }}>
        <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', letterSpacing: '.14em', textTransform: 'uppercase', color: color.boneDim, mb: 0.5 }}>
          Superadmin console
        </Typography>
        <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.5rem', color: color.bone, mb: 3 }}>
          Press Room
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mb: 3, borderBottom: `1px solid ${color.ruleSoft}` }}
        >
          <Tab label="Add property" />
          <Tab label="Add admin" />
          <Tab label="Assign role" />
        </Tabs>
        <Fade in={tab === 0} timeout={300} unmountOnExit>
          <Box sx={{ maxWidth: 480, mb: 4 }}>{tab === 0 && CreateWebsiteCard}</Box>
        </Fade>
        <Fade in={tab === 1} timeout={300} unmountOnExit>
          <Box sx={{ maxWidth: 480, mb: 4 }}>{tab === 1 && CreateAdminCard}</Box>
        </Fade>
        <Fade in={tab === 2} timeout={300} unmountOnExit>
          <Box sx={{ maxWidth: 480, mb: 4 }}>{tab === 2 && AssignRoleCard}</Box>
        </Fade>

        <Fade in={loading} unmountOnExit>
          <Box><Loader minHeight={300} /></Box>
        </Fade>

        {/* Staff ledger */}
        {!loading && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.15rem', color: color.bone, mb: 2 }}>
              Staff
            </Typography>
            <Paper sx={{ background: color.panel, border: `1px solid ${color.ruleSoft}`, clipPath: bevel(12) }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <TextField
                  size="small"
                  placeholder="Search staff"
                  value={userFilter}
                  onChange={e => setUserFilter(e.target.value)}
                  sx={{ mb: 2, width: { xs: '100%', sm: 280 } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: color.boneFaint, fontSize: 18 }} /></InputAdornment> }}
                />
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {isMobile && <TableCell />}
                      <TableCell>Callsign</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Properties</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedUsers.map(user => (
                      <React.Fragment key={user._id}>
                        <TableRow>
                          {isMobile && (
                            <TableCell>
                              <IconButton size="small" onClick={() => setExpandedUser(expandedUser === user._id ? null : user._id)} sx={{ color: color.cyan }}>
                                {expandedUser === user._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </IconButton>
                            </TableCell>
                          )}
                          <TableCell sx={{ fontFamily: font.mono, color: color.bone }}>{user.username}</TableCell>
                          <TableCell>
                            <Box component="span" sx={{
                              fontFamily: font.mono, fontSize: '.6875rem', px: '.5em', py: '.2em',
                              border: `1px solid ${user.role === 'superadmin' ? color.cyanDim : color.amberDim}`,
                              color: user.role === 'superadmin' ? color.cyan : color.amber,
                            }}>
                              {user.role}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: color.boneDim }}>
                            {user.role === 'superadmin'
                              ? 'All properties'
                              : user.websites && user.websites.length > 0
                              ? user.websites.map((site, idx) =>
                                  typeof site === 'object' && site !== null ? (
                                    <span key={site._id}>
                                      {site.name}
                                      <IconButton size="small" sx={{ ml: 0.5, color: color.amber }} onClick={() => handleRemoveWebsite(user._id, site._id)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                      {idx < user.websites.length - 1 ? ', ' : ''}
                                    </span>
                                  ) : (
                                    <span key={site}>{site}{idx < user.websites.length - 1 ? ', ' : ''}</span>
                                  )
                                )
                              : 'None'}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" title="Edit" sx={{ color: color.boneDim, '&:hover': { color: color.cyan } }} onClick={() => openEditUser(user)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {user.role !== 'superadmin' && (
                              <IconButton size="small" title="Remove" sx={{ color: color.red }} onClick={() => handleDeleteUser(user._id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                        {isMobile && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                              <Collapse in={expandedUser === user._id} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, background: color.panel2 }}>
                                  <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', color: color.boneFaint, mb: 1 }}>Properties</Typography>
                                  <Typography sx={{ color: color.bone }}>
                                    {user.role === 'superadmin' ? 'All properties' : (user.websites || []).map(s => s.name || s).join(', ') || 'None'}
                                  </Typography>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={userPage}
                onPageChange={(e, newPage) => setUserPage(newPage)}
                rowsPerPage={userRowsPerPage}
                onRowsPerPageChange={e => { setUserRowsPerPage(parseInt(e.target.value, 10)); setUserPage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ color: color.boneDim }}
              />
            </Paper>
          </Box>
        )}

        {/* Properties ledger */}
        {!loading && (
          <Box sx={{ mt: 5 }}>
            <Typography sx={{ fontFamily: font.display, fontWeight: 600, fontSize: '1.15rem', color: color.bone, mb: 2 }}>
              Properties
            </Typography>
            <Paper sx={{ background: color.panel, border: `1px solid ${color.ruleSoft}`, clipPath: bevel(12) }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <TextField
                  size="small"
                  placeholder="Search properties"
                  value={websiteFilter}
                  onChange={e => setWebsiteFilter(e.target.value)}
                  sx={{ mb: 2, width: { xs: '100%', sm: 280 } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: color.boneFaint, fontSize: 18 }} /></InputAdornment> }}
                />
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {isMobile && <TableCell />}
                      <TableCell>Name</TableCell>
                      <TableCell>Link</TableCell>
                      <TableCell>API endpoint</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedWebsites.map(site => {
                      const apiPath = `/api/blogs/website/${site._id}`;
                      return (
                        <React.Fragment key={site._id}>
                          <TableRow>
                            {isMobile && (
                              <TableCell>
                                <IconButton size="small" onClick={() => setExpandedWebsite(expandedWebsite === site._id ? null : site._id)} sx={{ color: color.cyan }}>
                                  {expandedWebsite === site._id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                            )}
                            <TableCell sx={{ color: color.bone }}>{site.name}</TableCell>
                            <TableCell>
                              <a href={site.link} target="_blank" rel="noopener noreferrer" style={{ color: color.cyan }}>
                                {site.link}
                              </a>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography component="code" sx={{ fontFamily: font.mono, fontSize: '.75rem', color: color.cyan, whiteSpace: 'nowrap' }}>
                                  {apiPath}
                                </Typography>
                                <IconButton size="small" onClick={() => handleCopyEndpoint(apiPath)} title="Copy endpoint" sx={{ color: color.boneFaint, '&:hover': { color: color.cyan } }}>
                                  <ContentCopyIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" sx={{ color: color.red }} onClick={() => handleDeleteWebsite(site._id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                          {isMobile && (
                            <TableRow>
                              <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                                <Collapse in={expandedWebsite === site._id} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, background: color.panel2 }}>
                                    <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', color: color.boneFaint, mb: 1 }}>API endpoint</Typography>
                                    <Typography sx={{ fontFamily: font.mono, fontSize: '.75rem', color: color.cyan, wordBreak: 'break-all' }}>{apiPath}</Typography>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredWebsites.length}
                page={websitePage}
                onPageChange={(e, newPage) => setWebsitePage(newPage)}
                rowsPerPage={websiteRowsPerPage}
                onRowsPerPageChange={e => { setWebsiteRowsPerPage(parseInt(e.target.value, 10)); setWebsitePage(0); }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{ color: color.boneDim }}
              />
            </Paper>
            <Typography sx={{ fontFamily: font.mono, fontSize: '.6875rem', color: color.boneFaint, mt: 1.5 }}>
              Base host: <Box component="span" sx={{ color: color.boneDim }}>{window.location.origin}</Box> &mdash; each property reads its own feed from this endpoint.
            </Typography>
          </Box>
        )}
        <Box sx={{ height: { xs: 40, sm: 80 } }} />
      </Box>

      {/* Edit staff dialog */}
      <Dialog open={!!editUserTarget} onClose={() => setEditUserTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: font.display, fontWeight: 600 }}>Edit staff</DialogTitle>
        <Box component="form" onSubmit={handleUpdateUser}>
          <DialogContent>
            <Stack spacing={2}>
              <TextField
                label="Callsign"
                value={editUserForm.username}
                onChange={e => setEditUserForm({ ...editUserForm, username: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="New passcode"
                type="password"
                value={editUserForm.password}
                onChange={e => setEditUserForm({ ...editUserForm, password: e.target.value })}
                helperText="Leave blank to keep the current passcode"
                fullWidth
              />
              {editUserForm.password && (
                <TextField
                  label="Confirm new passcode"
                  type="password"
                  value={editUserForm.confirmPassword}
                  onChange={e => setEditUserForm({ ...editUserForm, confirmPassword: e.target.value })}
                  fullWidth
                  required
                />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUserTarget(null)} variant="outlined">Cancel</Button>
            <Button type="submit" variant="contained" color="primary" disabled={savingUser}>
              {savingUser ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Snackbar
        open={!!notice}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {notice ? <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default AdminDashboard;
